import React, { useState, useCallback } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ToggleOffOutlinedIcon from "@mui/icons-material/ToggleOffOutlined";
import ToggleOnOutlinedIcon from "@mui/icons-material/ToggleOnOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const actionIcons = {
  edit: EditOutlinedIcon,
  deactivate: ToggleOffOutlinedIcon,
  activate: ToggleOnOutlinedIcon,
};

function ActionsPopover({ entity, items, onAction }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  const menuItems = items || [
    { key: "edit", label: "Edit" },
    { key: "deactivate", label: "Deactivate" },
    { key: "delete", label: "Delete", danger: true },
  ];

  const handleOpen = useCallback((e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleItemClick = useCallback(
    (actionKey) => {
      handleClose();
      if (onAction) {
        onAction(actionKey, entity);
      }
    },
    [onAction, entity, handleClose]
  );

  const dangerItems = menuItems.filter((i) => i.danger);
  const normalItems = menuItems.filter((i) => !i.danger);

  const getIcon = (item) => {
    if (item.danger) return DeleteOutlineIcon;
    const Icon = actionIcons[item.key.toLowerCase()];
    return Icon || null;
  };

  return (
    <>
      <Tooltip title="Actions" arrow>
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label="Row actions"
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          sx={{
            color: "text.secondary",
            "&:hover": {
              backgroundColor: alpha(primary, 0.08),
              color: primary,
            },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              minWidth: 160,
              borderRadius: 2,
              mt: 0.5,
              "& .MuiMenuItem-root": {
                borderRadius: 1,
                mx: 0.5,
                px: 1.5,
                py: 0.75,
                fontSize: "0.875rem",
              },
            },
          },
        }}
      >
        {normalItems.map((item) => {
          const Icon = getIcon(item);
          return (
            <MenuItem
              key={item.key}
              onClick={() => handleItemClick(item.key)}
              sx={{
                "&:hover": {
                  backgroundColor: alpha(primary, 0.08),
                  color: primary,
                  "& .MuiListItemIcon-root": { color: primary },
                },
              }}
            >
              {Icon && (
                <ListItemIcon sx={{ minWidth: 32, color: "text.secondary" }}>
                  <Icon fontSize="small" />
                </ListItemIcon>
              )}
              <ListItemText primaryTypographyProps={{ fontSize: "0.875rem" }}>
                {item.label}
              </ListItemText>
            </MenuItem>
          );
        })}

        {dangerItems.length > 0 && normalItems.length > 0 && (
          <Divider sx={{ my: 0.5 }} />
        )}

        {dangerItems.map((item) => {
          const Icon = getIcon(item);
          return (
            <MenuItem
              key={item.key}
              onClick={() => handleItemClick(item.key)}
              sx={{
                color: "error.main",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.error.main, 0.08),
                },
                "& .MuiListItemIcon-root": { color: "error.main" },
              }}
            >
              {Icon && (
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Icon fontSize="small" />
                </ListItemIcon>
              )}
              <ListItemText primaryTypographyProps={{ fontSize: "0.875rem" }}>
                {item.label}
              </ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default ActionsPopover;
